import SimpleSelect, { type SelectOption } from '@/components/ui/SimpleSelect';
import { useQuery } from '@tanstack/react-query';
import { businessService } from '@/services/business.service';
import { LTI_CONTENT_LABEL } from '@/services/lti-content.catalogue';
import type { LtiContentOptions } from '@/hooks/useLtiContentOptions';
import { LtiResourceType } from '@/types/lti.types';

interface Props {
  type: LtiResourceType;
  resourceId: string;
  onChange: (next: { type: LtiResourceType; resourceId: string }) => void;
  catalogue: LtiContentOptions;
  disabled?: boolean;
  /** Side by side in a card row, stacked in a form. */
  layout?: 'row' | 'stacked';
}

const TYPE_OPTIONS: SelectOption[] = [
  { value: LtiResourceType.COURSE, label: 'Course' },
  { value: LtiResourceType.CERTIFICATION, label: 'Certification' },
  { value: LtiResourceType.LEARNING_PATH, label: 'Learning Path' },
];

/** "What should this link open?" — a content type, then the content itself. */
export default function LtiContentChooser({
  type,
  resourceId,
  onChange,
  catalogue,
  disabled = false,
  layout = 'row',
}: Props) {
  const { data: businessData } = useQuery({
    queryKey: ['businessSettings'],
    queryFn: () => businessService.getBusinessSettings(),
    staleTime: 5 * 60 * 1000,
  });
  const companyName = businessData?.data?.company_name?.trim() || businessData?.company_name?.trim() || 'Skillzen';

  const items = catalogue.optionsFor(type);
  const loading = catalogue.isLoading(type);
  const label = LTI_CONTENT_LABEL[type];

  const contentOptions: SelectOption[] = (items ?? []).map(item => ({
    value: item.id,
    label: item.title,
  }));

  const changeType = (next: LtiResourceType) => {
    onChange({ type: next, resourceId: '' }); // the old choice is not in the new list
    void catalogue.load(next);
  };

  const placeholder = loading
    ? `Loading ${label}s…`
    : contentOptions.length
      ? `Choose a ${label}…`
      : `No published ${label}s`;

  return (
    <div className={layout === 'row' ? 'flex flex-col sm:flex-row gap-2' : 'space-y-3'}>
      <div className={layout === 'row' ? 'sm:w-44' : ''}>
        <SimpleSelect
          label={layout === 'stacked' ? 'Content type' : undefined}
          value={type}
          options={TYPE_OPTIONS}
          onChange={value => value && changeType(value as LtiResourceType)}
          isDisabled={disabled}
          isSearchable={false}
          aria-label="Content type"
        />
      </div>

      <div className={layout === 'row' ? 'sm:w-72' : ''}>
        <SimpleSelect
          label={layout === 'stacked' ? 'Content' : undefined}
          required={layout === 'stacked'}
          value={resourceId || undefined}
          options={contentOptions}
          placeholder={placeholder}
          onChange={value => onChange({ type, resourceId: value ? String(value) : '' })}
          isDisabled={disabled || loading || contentOptions.length === 0}
          isLoading={loading}
          aria-label={`${companyName} ${label}`}
        />
      </div>
    </div>
  );
}
